import type { RefObject } from "react"

import {
  useCreateReviewAnnotationTransport,
  useReviewAnnotationTransportInvalidation,
  useUpdateReviewAnnotationTransport,
} from "@/human-feedback/transport"

import { logger } from "@/lib/logger"
import { annotationHasValue, type Annotation } from "@/types/api/Annotation"

import { showAnnotationSaveErrorToast } from "../utils/annotationSaveError"

const log = logger("HumanFeedbackUpdate")

interface UseHumanFeedbackAnnotationMutationsProps {
  initialAnnotation?: Annotation | null
  latestAnnotation: RefObject<Annotation | null>
  setCurrentAnnotation: (annotation: Annotation | null) => void
  onValueOptimisticallyChanged: RefObject<
    ((hasValue: boolean) => void) | undefined
  >
  projectId?: string
}

export function useHumanFeedbackAnnotationMutations({
  initialAnnotation,
  latestAnnotation,
  setCurrentAnnotation,
  onValueOptimisticallyChanged,
  projectId,
}: UseHumanFeedbackAnnotationMutationsProps) {
  const invalidateAnnotationTransportQueries =
    useReviewAnnotationTransportInvalidation()

  const createAnnotation = useCreateReviewAnnotationTransport({
    onSuccess: (newAnnotation) => {
      const optimisticAnnotation = latestAnnotation.current
      const mergedAnnotation = {
        ...newAnnotation,
        ground_truth_float_value:
          optimisticAnnotation?.ground_truth_float_value ??
          newAnnotation.ground_truth_float_value,
        ground_truth_string_value:
          optimisticAnnotation?.ground_truth_string_value ??
          newAnnotation.ground_truth_string_value,
        ground_truth_set_value:
          optimisticAnnotation?.ground_truth_set_value ??
          newAnnotation.ground_truth_set_value,
        annotations: {
          ...newAnnotation.annotations,
          ...optimisticAnnotation?.annotations,
        },
      }

      setCurrentAnnotation(mergedAnnotation)
      void invalidateAnnotationTransportQueries(
        newAnnotation.simulation_output_id,
        projectId,
      )
    },
    onError: (error) => {
      setCurrentAnnotation(initialAnnotation || null)
      onValueOptimisticallyChanged.current?.(
        initialAnnotation ? annotationHasValue(initialAnnotation) : false,
      )
      showAnnotationSaveErrorToast(error)
      log.error("Failed to create annotation", error)
    },
  })

  const updateAnnotation = useUpdateReviewAnnotationTransport({
    onSuccess: (updatedAnnotation) => {
      setCurrentAnnotation(updatedAnnotation)
      void invalidateAnnotationTransportQueries(
        updatedAnnotation.simulation_output_id,
        projectId,
      )
    },
    onError: (error) => {
      setCurrentAnnotation(initialAnnotation || null)
      onValueOptimisticallyChanged.current?.(
        initialAnnotation ? annotationHasValue(initialAnnotation) : false,
      )
      showAnnotationSaveErrorToast(error)
      log.error("Failed to update annotation", error)
    },
  })

  return { createAnnotation, updateAnnotation }
}
